// import { IS_DEV } from "@/__main__/constants.mjs";
import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { GAME_SYMBOL_MINECRAFT } from "@/game-minecraft/definition-symbol.mjs";
import type { ChunkData } from "@/game-minecraft/models/ipc.mjs";
import type {
  Block,
  Entity,
  Item,
} from "@/game-minecraft/models/model-wiki.mjs";
import { overlayRoutes } from "@/game-minecraft/overlay-routes.mjs";
import { OVERLAY_IDS } from "@/game-minecraft/refs.mjs";
import { routes } from "@/game-minecraft/routes.mjs";
import GameIconBoxMinecraft from "@/inline-assets/game-icon-box-minecraft.svg";
import GameIconShapeMinecraft from "@/inline-assets/game-icon-shape-minecraft.svg";

export { GAME_SYMBOL_MINECRAFT };

const MINECRAFT_OVERLAY_SETTINGS = {
  isMiniMapEnabled: true,
  isBlockSightEnabled: true,
  positions: Object.values(OVERLAY_IDS).reduce((acc, id) => {
    acc[id] = { x: 0, y: 0, isDragged: false };
    return acc;
  }, {}),
};

const GameMinecraft = {
  gameSymbol: GAME_SYMBOL_MINECRAFT,
  friendlyName: ["common:games.minecraft.short", "Minecraft"],
  fullName: ["common:games.minecraft.long", "Minecraft"],
  shortName: "minecraft" as const,
  gameColor: "#68B74A",

  iconShape: GameIconShapeMinecraft,
  iconBox: GameIconBoxMinecraft,
  icon: `${appURLs.CDN}/blitz/ui/images/icons/games/small/MC-small.webp`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/minecraft-min.webp`,

  isActive: true,
  preferFriendlyName: false as const,

  meta: {
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/minecraft.webp`,
    homeData: () => import("@/game-minecraft/fetches/homepage.mjs"),
  },
  settings: () => Promise.resolve({ default: () => null }),
  navLinks: [
    {
      href: `/minecraft/database`,
      text: ["common:navigation.database", "Database"],
      menu: [
        [
          {
            category: ["minecraft:wiki.blocks", "Blocks"],
            items: [
              {
                text: ["minecraft.wiki.allBlocks", "All Blocks"],
                href: "/minecraft/database/blocks",
              },
            ],
          },
          {
            category: ["minecraft:wiki.items", "Items"],
            items: [
              {
                text: ["minecraft.wiki.allItems", "All Items"],
                href: "/minecraft/database/items",
              },
            ],
          },
          {
            category: ["minecraft:wiki.mobs", "Mobs"],
            items: [
              {
                text: ["minecraft.wiki.allMobs", "All Mobs"],
                href: "/minecraft/database/mobs",
              },
            ],
          },
        ],
      ],
    },
    {
      href: `/overlays/minecraft`,
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
    {
      href: `/minecraft/news`,
      text: ["common:news.title", "News"],
    },
  ],
  routes,
  overlayRoutes,
  overlaySettingsKey: "mcOverlaySettings",
  overlays: {
    miniMap: {
      key: ["isMiniMapEnabled"],
      title: ["overlays:minecraft.miniMap.title", "Minimap"],
      description: [
        "overlays:minecraft.miniMap.description",
        "See your coordinates and a surrounding map in realtime!",
      ],
      tags: ["utility"],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/mcOverlay/blitz-minimap-tall.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/mcOverlay/blitz-minimap-small.webp`,
      },
      date: new Date(2025, 1, 5),
      conflictDisclaimer: [
        "overlays:minecraft.miniMap.disclaimer",
        "Currently only available in Minecraft Bedrock. Does not currently work in the Nether.",
      ],
    },
    blockSight: {
      key: ["isBlockSightEnabled"],
      title: ["overlays:minecraft.blockSight.title", "Block Sight"],
      description: [
        "overlays:minecraft.blockSight.description",
        "Get realtime insight on the block and entity you are looking at.",
      ],
      tags: ["utility"],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/mcOverlay/blitz-blocksight-tall.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/mcOverlay/blitz-blocksight-small.webp`,
      },
      date: new Date(2025, 1, 5),
      conflictDisclaimer: [
        "overlays:minecraft.blockSight.disclaimer",
        "Currently only available in Minecraft Bedrock.",
      ],
    },
  },
  overlaySettings: MINECRAFT_OVERLAY_SETTINGS,
  homepageFeatures: [
    "blitz-search-hidden",
    {
      type: "contentful-articles",
      title: ["minecraft:contentfularticles.title", "News & Patch Notes"],
      description: [
        "minecraft:contentfularticles.description",
        "Stay up to date with the latest news, patch notes, and much more.",
      ],
      cta: {
        href: "/minecraft/news",
        title: ["common:contentfularticles.viewAllNews", "View All News"],
      },
    },
    {
      type: "contentful-grid",
      title: ["minecraft:contentfulgrid.title", "Latest Guides"],
      description: [
        "minecraft:contentfulgrid.description",
        "Stay up to date with the latest guides!",
      ],
    },
    "blitz-game-features",
  ],
  IPC: () => import("@/game-minecraft/init.mjs"),
} satisfies GameDefinition<typeof GAME_SYMBOL_MINECRAFT>;

export default GameMinecraft;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    minecraft: {
      wiki: {
        blocks: Record<string, Block>;
        items: Record<string, Item>;
        entities: Record<string, Entity>;
        version: string[];
      };
    };
  }

  interface VolatileState {
    mcCurrentBlock?: {
      referenceId: string;
      pos: [number, number, number];
    };
    mcHeldItemId?: string | null;
    mcChunkData?: ChunkData;
    mcEntityData?: { name: string; pos: readonly [number, number, number] }[];
    mcPlayerData?: {
      pos: readonly [number, number, number];
      /** [yaw, pitch]: yaw is -180.0 for north, -90.0 for east; pitch is -90.0 for straight up, 90.0 for straight down */
      rot: readonly [number, number];
    };
    mcGuiOpen?: boolean;
    mcLastUpdateAt?: number;
    mcCollapse?: boolean;
  }
}
